import type { User } from '@/types/auth';
import { getDemoUser } from './config';
import { DEMO_CORE_QUESTIONNAIRE_LIST } from './coreQuestionnaireList';

type DemoClient = Record<string, unknown> & {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  status: string;
  location: string;
};

type DemoAppointment = Record<string, unknown> & {
  id: string;
  clientId: string;
  clientName: string;
  clientEmail: string;
  title: string;
  status: string;
  appointmentType: string;
  startTime: string;
  endTime: string;
};

type DemoInvoice = Record<string, unknown> & {
  id: string;
  invoiceNo: string;
  clientId: string;
  clientName: string;
  status: string;
  invoiceStatus: string;
  totalDue: number;
  currency: string;
  dueDate: string;
};

export const DEMO_NEW_CLIENT_HEALTH_PROFILE_ID = 'demo-core-intake';
const DEMO_NEW_CLIENT_HEALTH_PROFILE_TITLE = 'New Client Health Profile';

type DemoQuestionnaireInstance = {
  instanceId: string;
  questionnaireId: string;
  questionnaireTitle: string;
  questionnaireName: string;
  clientId: string;
  nutritionistId: string;
  status: 'pending' | 'in_progress' | 'completed';
  hasResponse: boolean;
  createdAt: string;
  completedAt?: string;
  submittedAt?: string;
  actualQuestionnaireQuestionCount: number;
  initialQuestionCount: number;
  answeredQuestionCount: number;
  responses: null;
};

const isoInDays = (days: number, hour = 10, minute = 0) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  date.setHours(hour, minute, 0, 0);
  return date.toISOString();
};

const ymdInDays = (days: number) => isoInDays(days).slice(0, 10);

const addMinutes = (iso: string, minutes: number) =>
  new Date(new Date(iso).getTime() + minutes * 60_000).toISOString();

const demoMacroRanges = (calories: number) => ({
  protein: { min: Math.round((calories * 0.2) / 4), max: Math.round((calories * 0.3) / 4), unit: 'g' },
  carbs: { min: Math.round((calories * 0.4) / 4), max: Math.round((calories * 0.55) / 4), unit: 'g' },
  fat: { min: Math.round((calories * 0.2) / 9), max: Math.round((calories * 0.35) / 9), unit: 'g' },
  fiber: { min: 25, max: 35, unit: 'g' },
});

const demoClientGoals = (calories: number) => ({
  eerGoalCalories: calories,
  goalCalories: calories,
  proteinGoalMin: Math.round((calories * 0.2) / 4),
  proteinGoalMax: Math.round((calories * 0.3) / 4),
  carbsGoalMin: Math.round((calories * 0.4) / 4),
  carbsGoalMax: Math.round((calories * 0.55) / 4),
  fatGoalMin: Math.round((calories * 0.2) / 9),
  fatGoalMax: Math.round((calories * 0.35) / 9),
  fiberGoalGrams: 30,
  waterGoalLitres: 2.4,
});

const baseQuestionnaireInstances = (): DemoQuestionnaireInstance[] => [
  {
    instanceId: 'demo-questionnaire-instance-completed',
    questionnaireId: DEMO_NEW_CLIENT_HEALTH_PROFILE_ID,
    questionnaireTitle: DEMO_NEW_CLIENT_HEALTH_PROFILE_TITLE,
    questionnaireName: DEMO_NEW_CLIENT_HEALTH_PROFILE_TITLE,
    clientId: 'demo-client-1',
    nutritionistId: 'demo-nutritionist',
    status: 'completed',
    hasResponse: true,
    createdAt: isoInDays(-4, 10, 0),
    completedAt: isoInDays(-2, 14, 0),
    submittedAt: isoInDays(-2, 14, 0),
    actualQuestionnaireQuestionCount: 35,
    initialQuestionCount: 35,
    answeredQuestionCount: 35,
    responses: null,
  },
  {
    instanceId: 'demo-questionnaire-instance-pending',
    questionnaireId: DEMO_NEW_CLIENT_HEALTH_PROFILE_ID,
    questionnaireTitle: DEMO_NEW_CLIENT_HEALTH_PROFILE_TITLE,
    questionnaireName: DEMO_NEW_CLIENT_HEALTH_PROFILE_TITLE,
    clientId: 'demo-client-2',
    nutritionistId: 'demo-nutritionist',
    status: 'pending',
    hasResponse: false,
    createdAt: isoInDays(-1, 11, 0),
    actualQuestionnaireQuestionCount: 35,
    initialQuestionCount: 35,
    answeredQuestionCount: 0,
    responses: null,
  },
];

const baseClients = (): DemoClient[] => [
  {
    id: 'demo-client-1',
    schemaVersion: 2,
    nutritionistId: 'demo-nutritionist',
    firstName: 'Amelia',
    lastName: 'Hart',
    fullName: 'Amelia Hart',
    email: 'amelia.hart@example.com',
    phone: '7700900101',
    phoneCountryCode: '+44',
    location: 'London',
    status: 'active',
    dateOfBirth: '1989-04-12',
    gender: 'female',
    sexAtBirth: 'female',
    heightCm: 168,
    weightKg: 72,
    targetWeightKg: 66,
    bmi: 25.5,
    bmiCategory: 'overweight',
    bmiCategoryDisplay: 'Overweight',
    bmr: 1458,
    eer: 2140,
    activityLevel: 'moderate',
    medicalConditions: 'Type 2 diabetes',
    allergies: ['Peanuts'],
    dietaryPreferences: ['Mediterranean'],
    healthGoals: ['Improve glucose control', 'Lose body fat'],
    questionnairesSentCount: 3,
    questionnaireProgress: 82,
    upcomingSessionDate: isoInDays(1, 9, 30),
    lastSessionDate: isoInDays(-9, 11, 0),
    invoiceStatus: 'due',
    createdAt: '2026-05-01T10:00:00.000Z',
    updatedAt: new Date().toISOString(),
    targetCalories: 1840,
    macrosRanges: demoMacroRanges(1840),
    clientGoals: demoClientGoals(1840),
    calculationDetails: {
      formulaUsed: 'henry_oxford',
      formulaDisplayName: 'Henry Oxford',
      palUsed: 1.55,
      weightGoalAdjustment: -300,
      growthFactorApplied: false,
      source: 'bmr_x_pal',
      calculatedAt: new Date().toISOString(),
    },
  },
  {
    id: 'demo-client-2',
    schemaVersion: 2,
    nutritionistId: 'demo-nutritionist',
    firstName: 'Noah',
    lastName: 'Patel',
    fullName: 'Noah Patel',
    email: 'noah.patel@example.com',
    phone: '7700900102',
    phoneCountryCode: '+44',
    location: 'Manchester',
    status: 'active',
    dateOfBirth: '1994-09-20',
    gender: 'male',
    sexAtBirth: 'male',
    heightCm: 180,
    weightKg: 83,
    targetWeightKg: 80,
    bmi: 25.6,
    bmiCategory: 'overweight',
    bmiCategoryDisplay: 'Overweight',
    bmr: 1790,
    eer: 2740,
    activityLevel: 'active',
    medicalConditions: 'None',
    allergies: [],
    dietaryPreferences: ['High protein'],
    healthGoals: ['Build lean mass'],
    questionnairesSentCount: 2,
    questionnaireProgress: 64,
    upcomingSessionDate: isoInDays(3, 14, 0),
    lastSessionDate: isoInDays(-12, 13, 30),
    invoiceStatus: 'paid',
    createdAt: '2026-04-18T10:00:00.000Z',
    updatedAt: new Date().toISOString(),
    targetCalories: 2890,
    macrosRanges: demoMacroRanges(2890),
    clientGoals: demoClientGoals(2890),
    calculationDetails: {
      formulaUsed: 'henry_oxford',
      formulaDisplayName: 'Henry Oxford',
      palUsed: 1.7,
      weightGoalAdjustment: 150,
      growthFactorApplied: false,
      source: 'bmr_x_pal',
      calculatedAt: new Date().toISOString(),
    },
  },
  {
    id: 'demo-client-3',
    schemaVersion: 2,
    nutritionistId: 'demo-nutritionist',
    firstName: 'Sofia',
    lastName: 'Reed',
    fullName: 'Sofia Reed',
    email: 'sofia.reed@example.com',
    phone: '7700900103',
    phoneCountryCode: '+44',
    location: 'Bristol',
    status: 'suspended',
    dateOfBirth: '1978-02-04',
    gender: 'female',
    sexAtBirth: 'female',
    heightCm: 162,
    weightKg: 69,
    targetWeightKg: 67,
    bmi: 26.3,
    bmiCategory: 'overweight',
    bmiCategoryDisplay: 'Overweight',
    bmr: 1320,
    eer: 1880,
    activityLevel: 'light',
    medicalConditions: 'Hypertension',
    allergies: ['Shellfish'],
    dietaryPreferences: ['Low sodium'],
    healthGoals: ['Reduce blood pressure'],
    questionnairesSentCount: 1,
    questionnaireProgress: 40,
    upcomingSessionDate: null,
    lastSessionDate: isoInDays(-22, 10, 0),
    invoiceStatus: null,
    createdAt: '2026-03-20T10:00:00.000Z',
    updatedAt: new Date().toISOString(),
    targetCalories: 1780,
    macrosRanges: demoMacroRanges(1780),
    clientGoals: demoClientGoals(1780),
  },
  {
    id: 'demo-client-pending',
    schemaVersion: 2,
    nutritionistId: 'demo-nutritionist',
    firstName: 'Jordan',
    lastName: 'Hale',
    fullName: 'Jordan Hale',
    email: 'jordan.hale@example.com',
    phone: '7700900199',
    phoneCountryCode: '+44',
    location: 'Manchester',
    status: 'pending_deletion',
    dateOfBirth: '1991-08-19',
    gender: 'male',
    sexAtBirth: 'male',
    heightCm: 178,
    weightKg: 81,
    targetWeightKg: 76,
    bmi: 25.6,
    bmiCategory: 'overweight',
    bmiCategoryDisplay: 'Overweight',
    bmr: 1720,
    eer: 2400,
    activityLevel: 'moderate',
    medicalConditions: '',
    allergies: [],
    dietaryPreferences: [],
    healthGoals: ['Maintain energy'],
    questionnairesSentCount: 1,
    questionnaireProgress: 20,
    upcomingSessionDate: null,
    lastSessionDate: isoInDays(-18, 10, 0),
    invoiceStatus: null,
    createdAt: '2026-04-02T10:00:00.000Z',
    updatedAt: isoInDays(-2, 9, 0),
    targetCalories: 2200,
    macrosRanges: demoMacroRanges(2200),
    clientGoals: demoClientGoals(2200),
    accountDeletion: {
      status: 'pending_deletion',
      requestedAt: isoInDays(-2, 9, 0),
      requestedBy: 'client',
      purgeAt: isoInDays(12, 9, 0),
      recoverableUntil: isoInDays(12, 9, 0),
      previousStatus: 'active',
    },
    message: 'everything_taken_care_of',
  },
  {
    id: 'demo-client-4',
    schemaVersion: 2,
    nutritionistId: 'demo-nutritionist',
    firstName: 'Priya',
    lastName: 'Sharma',
    fullName: 'Priya Sharma',
    email: 'priya.sharma@example.com',
    phone: '7700900104',
    phoneCountryCode: '+44',
    location: 'Leeds',
    status: 'active',
    dateOfBirth: '1992-11-08',
    gender: 'female',
    sexAtBirth: 'female',
    heightCm: 160,
    weightKg: 64,
    targetWeightKg: 60,
    bmi: 25.0,
    bmiCategory: 'overweight',
    bmiCategoryDisplay: 'Overweight',
    bmr: 1350,
    eer: 1950,
    activityLevel: 'moderate',
    medicalConditions: 'PCOS',
    allergies: [],
    dietaryPreferences: ['Vegetarian'],
    healthGoals: ['Regulate cycles', 'Steady energy'],
    questionnairesSentCount: 2,
    questionnaireProgress: 75,
    upcomingSessionDate: isoInDays(2, 11, 0),
    lastSessionDate: isoInDays(-7, 11, 0),
    invoiceStatus: 'paid',
    createdAt: '2026-05-14T10:00:00.000Z',
    updatedAt: new Date().toISOString(),
    targetCalories: 1750,
    macrosRanges: demoMacroRanges(1750),
    clientGoals: demoClientGoals(1750),
  },
  {
    id: 'demo-client-5',
    schemaVersion: 2,
    nutritionistId: 'demo-nutritionist',
    firstName: 'Marcus',
    lastName: 'Webb',
    fullName: 'Marcus Webb',
    email: 'marcus.webb@example.com',
    phone: '7700900105',
    phoneCountryCode: '+44',
    location: 'Birmingham',
    status: 'active',
    dateOfBirth: '1998-03-27',
    gender: 'male',
    sexAtBirth: 'male',
    heightCm: 186,
    weightKg: 79,
    targetWeightKg: 84,
    bmi: 22.8,
    bmiCategory: 'normal',
    bmiCategoryDisplay: 'Normal',
    bmr: 1880,
    eer: 3260,
    activityLevel: 'very_active',
    medicalConditions: 'None',
    allergies: ['Tree nuts'],
    dietaryPreferences: ['High protein'],
    healthGoals: ['Marathon preparation', 'Increase carbohydrate periodisation'],
    questionnairesSentCount: 1,
    questionnaireProgress: 100,
    upcomingSessionDate: isoInDays(5, 16, 30),
    lastSessionDate: isoInDays(-4, 16, 30),
    invoiceStatus: 'due',
    createdAt: '2026-06-02T10:00:00.000Z',
    updatedAt: new Date().toISOString(),
    targetCalories: 3260,
    macrosRanges: demoMacroRanges(3260),
    clientGoals: demoClientGoals(3260),
  },
  {
    id: 'demo-client-6',
    schemaVersion: 2,
    nutritionistId: 'demo-nutritionist',
    firstName: 'Elena',
    lastName: 'Novak',
    fullName: 'Elena Novak',
    email: 'elena.novak@example.com',
    phone: '7700900106',
    phoneCountryCode: '+44',
    location: 'Edinburgh',
    status: 'active',
    dateOfBirth: '1985-07-19',
    gender: 'female',
    sexAtBirth: 'female',
    heightCm: 170,
    weightKg: 66,
    targetWeightKg: 66,
    bmi: 22.8,
    bmiCategory: 'normal',
    bmiCategoryDisplay: 'Normal',
    bmr: 1420,
    eer: 2050,
    activityLevel: 'light',
    medicalConditions: 'IBS',
    allergies: [],
    dietaryPreferences: ['Low FODMAP'],
    healthGoals: ['Identify trigger foods', 'Reduce bloating'],
    questionnairesSentCount: 4,
    questionnaireProgress: 90,
    upcomingSessionDate: isoInDays(1, 15, 0),
    lastSessionDate: isoInDays(-14, 15, 0),
    invoiceStatus: 'paid',
    createdAt: '2026-02-09T10:00:00.000Z',
    updatedAt: new Date().toISOString(),
    targetCalories: 2050,
    macrosRanges: demoMacroRanges(2050),
    clientGoals: demoClientGoals(2050),
  },
];

const appointmentStart = isoInDays(1, 9, 30);
const baseAppointments = (): DemoAppointment[] => [
  {
    id: 'demo-appointment-1',
    clientId: 'demo-client-1',
    clientName: 'Amelia Hart',
    clientEmail: 'amelia.hart@example.com',
    title: 'Follow-up consultation',
    type: 'Follow-up consultation',
    status: 'active',
    appointmentType: 'online',
    mode: 'online',
    location: 'Google Meet',
    meetingLink: 'https://meet.google.com/demo-calorie-science',
    date: appointmentStart,
    startTime: appointmentStart,
    endTime: addMinutes(appointmentStart, 45),
    duration: 45,
    description: 'Review glucose log and meal-plan adherence.',
  },
  {
    id: 'demo-appointment-2',
    clientId: 'demo-client-2',
    clientName: 'Noah Patel',
    clientEmail: 'noah.patel@example.com',
    title: 'Sports nutrition check-in',
    type: 'Sports nutrition check-in',
    status: 'active',
    appointmentType: 'offline',
    mode: 'in_person',
    location: 'Harbour Nutrition Clinic',
    date: isoInDays(3, 14, 0),
    startTime: isoInDays(3, 14, 0),
    endTime: isoInDays(3, 15, 0),
    duration: 60,
    description: 'Adjust protein targets for training block.',
  },
  {
    id: 'demo-appointment-3',
    clientId: 'demo-client-6',
    clientName: 'Elena Novak',
    clientEmail: 'elena.novak@example.com',
    title: 'Low-FODMAP reintroduction review',
    type: 'Low-FODMAP reintroduction review',
    status: 'active',
    appointmentType: 'online',
    mode: 'online',
    location: 'Google Meet',
    meetingLink: 'https://meet.google.com/demo-calorie-science-2',
    date: isoInDays(1, 15, 0),
    startTime: isoInDays(1, 15, 0),
    endTime: isoInDays(1, 15, 45),
    duration: 45,
    description: 'Review symptom diary and plan the next reintroduction phase.',
  },
  {
    id: 'demo-appointment-4',
    clientId: 'demo-client-4',
    clientName: 'Priya Sharma',
    clientEmail: 'priya.sharma@example.com',
    title: 'PCOS nutrition follow-up',
    type: 'PCOS nutrition follow-up',
    status: 'active',
    appointmentType: 'offline',
    mode: 'in_person',
    location: 'Harbour Nutrition Clinic',
    date: isoInDays(2, 11, 0),
    startTime: isoInDays(2, 11, 0),
    endTime: isoInDays(2, 11, 45),
    duration: 45,
    description: 'Discuss questionnaire results and meal-plan adherence.',
  },
  {
    id: 'demo-appointment-5',
    clientId: 'demo-client-5',
    clientName: 'Marcus Webb',
    clientEmail: 'marcus.webb@example.com',
    title: 'Race-week fuelling strategy',
    type: 'Race-week fuelling strategy',
    status: 'active',
    appointmentType: 'online',
    mode: 'online',
    location: 'Google Meet',
    meetingLink: 'https://meet.google.com/demo-calorie-science-3',
    date: isoInDays(5, 16, 30),
    startTime: isoInDays(5, 16, 30),
    endTime: isoInDays(5, 17, 30),
    duration: 60,
    description: 'Carb-loading plan and hydration targets for the half marathon.',
  },
];

const baseInvoices = (): DemoInvoice[] => [
  {
    id: 'demo-invoice-1',
    invoiceNo: 'INV-DEMO-1001',
    clientId: 'demo-client-1',
    clientName: 'Amelia Hart',
    title: 'Follow-up consultation',
    status: 'due',
    invoiceStatus: 'due',
    totalDue: 95,
    amount: 95,
    currency: 'GBP',
    dueDate: isoInDays(2, 0, 0),
    invoiceDate: isoInDays(-5, 0, 0),
    lineItems: [{ description: 'Nutrition consultation', qty: 1, unitPrice: 95 }],
  },
  {
    id: 'demo-invoice-5',
    invoiceNo: 'INV-DEMO-0998',
    clientId: 'demo-client-1',
    clientName: 'Amelia Hart',
    title: 'Initial consultation',
    status: 'overdue',
    invoiceStatus: 'overdue',
    totalDue: 120,
    amount: 120,
    currency: 'GBP',
    dueDate: isoInDays(-12, 0, 0),
    invoiceDate: isoInDays(-26, 0, 0),
    lineItems: [{ description: 'Initial nutrition consultation', qty: 1, unitPrice: 120 }],
  },
  {
    id: 'demo-invoice-2',
    invoiceNo: 'INV-DEMO-1000',
    clientId: 'demo-client-2',
    clientName: 'Noah Patel',
    title: 'Meal plan package',
    status: 'paid',
    invoiceStatus: 'paid',
    totalDue: 180,
    amount: 180,
    currency: 'GBP',
    dueDate: isoInDays(-8, 0, 0),
    paidAt: isoInDays(-6, 0, 0),
    invoiceDate: isoInDays(-14, 0, 0),
    lineItems: [{ description: 'Personalized meal plan', qty: 1, unitPrice: 180 }],
  },
  {
    id: 'demo-invoice-3',
    invoiceNo: 'INV-DEMO-1002',
    clientId: 'demo-client-4',
    clientName: 'Priya Sharma',
    title: 'PCOS nutrition package',
    status: 'paid',
    invoiceStatus: 'paid',
    totalDue: 240,
    amount: 240,
    currency: 'GBP',
    dueDate: isoInDays(-3, 0, 0),
    paidAt: isoInDays(-4, 0, 0),
    invoiceDate: isoInDays(-10, 0, 0),
    lineItems: [{ description: '3-session PCOS support package', qty: 1, unitPrice: 240 }],
  },
  {
    id: 'demo-invoice-deleted-client',
    invoiceNo: 'INV-DEMO-0988',
    clientId: 'demo-client-removed',
    clientName: 'Riley Chen',
    clientDeleted: true,
    title: 'Follow-up consultation',
    status: 'paid',
    invoiceStatus: 'paid',
    totalDue: 95,
    amount: 95,
    currency: 'GBP',
    dueDate: isoInDays(-20, 0, 0),
    paidAt: isoInDays(-18, 0, 0),
    invoiceDate: isoInDays(-28, 0, 0),
    lineItems: [{ description: 'Nutrition consultation', qty: 1, unitPrice: 95 }],
  },
  {
    id: 'demo-invoice-4',
    invoiceNo: 'INV-DEMO-1003',
    clientId: 'demo-client-5',
    clientName: 'Marcus Webb',
    title: 'Sports nutrition consultation',
    status: 'due',
    invoiceStatus: 'due',
    totalDue: 110,
    amount: 110,
    currency: 'GBP',
    dueDate: isoInDays(4, 0, 0),
    invoiceDate: isoInDays(-2, 0, 0),
    lineItems: [{ description: 'Endurance fuelling consultation', qty: 1, unitPrice: 110 }],
  },
];

const baseRecipes = () => [
  {
    id: 'demo-recipe-1',
    title: 'Lemon Herb Salmon Bowl',
    name: 'Lemon Herb Salmon Bowl',
    servings: 2,
    mealTypes: ['Lunch', 'Dinner'],
    cuisineTypes: ['Mediterranean'],
    dietLabels: ['High protein'],
    allergens: ['Fish'],
    intoleranceTags: [],
    totalTimeMinutes: 25,
    totalCalories: 540,
    totalProtein: 38,
    totalCarbs: 46,
    totalFat: 22,
    nutritionPerServing: { calories: 270, protein: 19, carbohydrate: 23, fat: 11 },
    imageUrl: 'https://images.unsplash.com/photo-1467003909585-2f8a72700288?auto=format&fit=crop&w=900&q=80',
    isOwn: true,
    canEdit: true,
    isVerified: true,
  },
  {
    id: 'demo-recipe-2',
    title: 'Berry Greek Yogurt Parfait',
    name: 'Berry Greek Yogurt Parfait',
    servings: 1,
    mealTypes: ['Breakfast', 'Snack'],
    cuisineTypes: ['British'],
    dietLabels: ['Vegetarian'],
    allergens: ['Milk'],
    intoleranceTags: ['Lactose'],
    totalTimeMinutes: 8,
    totalCalories: 310,
    totalProtein: 24,
    totalCarbs: 36,
    totalFat: 8,
    nutritionPerServing: { calories: 310, protein: 24, carbohydrate: 36, fat: 8 },
    imageUrl: 'https://images.unsplash.com/photo-1488477181946-6428a0291777?auto=format&fit=crop&w=900&q=80',
    isOwn: false,
    canEdit: false,
    isVerified: true,
  },
  {
    id: 'demo-recipe-3',
    title: 'Overnight Oats with Berries',
    name: 'Overnight Oats with Berries',
    servings: 1,
    mealTypes: ['Breakfast'],
    cuisineTypes: ['British'],
    dietLabels: ['Vegetarian', 'High fibre'],
    allergens: ['Milk'],
    intoleranceTags: [],
    totalTimeMinutes: 5,
    totalCalories: 380,
    totalProtein: 16,
    totalCarbs: 58,
    totalFat: 9,
    nutritionPerServing: { calories: 380, protein: 16, carbohydrate: 58, fat: 9 },
    imageUrl: 'https://images.unsplash.com/photo-1517673400267-0251440c45dc?auto=format&fit=crop&w=900&q=80',
    isOwn: true,
    canEdit: true,
    isVerified: true,
  },
  {
    id: 'demo-recipe-4',
    title: 'Chicken Quinoa Power Bowl',
    name: 'Chicken Quinoa Power Bowl',
    servings: 2,
    mealTypes: ['Lunch'],
    cuisineTypes: ['Mediterranean'],
    dietLabels: ['High protein'],
    allergens: [],
    intoleranceTags: [],
    totalTimeMinutes: 30,
    totalCalories: 620,
    totalProtein: 42,
    totalCarbs: 55,
    totalFat: 21,
    nutritionPerServing: { calories: 310, protein: 21, carbohydrate: 28, fat: 10 },
    imageUrl: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=900&q=80',
    isOwn: true,
    canEdit: true,
    isVerified: true,
  },
  {
    id: 'demo-recipe-5',
    title: 'Baked Cod with Roast Vegetables',
    name: 'Baked Cod with Roast Vegetables',
    servings: 2,
    mealTypes: ['Dinner'],
    cuisineTypes: ['British'],
    dietLabels: ['Low fat'],
    allergens: ['Fish'],
    intoleranceTags: [],
    totalTimeMinutes: 35,
    totalCalories: 480,
    totalProtein: 44,
    totalCarbs: 38,
    totalFat: 16,
    nutritionPerServing: { calories: 240, protein: 22, carbohydrate: 19, fat: 8 },
    imageUrl: 'https://images.unsplash.com/photo-1519708227418-c8fd9a32b7a2?auto=format&fit=crop&w=900&q=80',
    isOwn: false,
    canEdit: false,
    isVerified: true,
  },
  {
    id: 'demo-recipe-6',
    title: 'Tofu Stir-Fry with Brown Rice',
    name: 'Tofu Stir-Fry with Brown Rice',
    servings: 2,
    mealTypes: ['Dinner'],
    cuisineTypes: ['Asian'],
    dietLabels: ['Vegan', 'High fibre'],
    allergens: ['Soy'],
    intoleranceTags: [],
    totalTimeMinutes: 25,
    totalCalories: 560,
    totalProtein: 28,
    totalCarbs: 68,
    totalFat: 18,
    nutritionPerServing: { calories: 280, protein: 14, carbohydrate: 34, fat: 9 },
    imageUrl: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=900&q=80',
    isOwn: true,
    canEdit: true,
    isVerified: false,
  },
  {
    id: 'demo-recipe-7',
    title: 'Apple Cinnamon Energy Bites',
    name: 'Apple Cinnamon Energy Bites',
    servings: 4,
    mealTypes: ['Snack'],
    cuisineTypes: ['British'],
    dietLabels: ['Vegetarian'],
    allergens: ['Tree nuts'],
    intoleranceTags: [],
    totalTimeMinutes: 15,
    totalCalories: 640,
    totalProtein: 16,
    totalCarbs: 76,
    totalFat: 30,
    nutritionPerServing: { calories: 160, protein: 4, carbohydrate: 19, fat: 7.5 },
    imageUrl: 'https://images.unsplash.com/photo-1490474418585-ba9bad8fd0ea?auto=format&fit=crop&w=900&q=80',
    isOwn: true,
    canEdit: true,
    isVerified: false,
  },
  {
    id: 'demo-recipe-8',
    title: 'Shakshuka with Feta',
    name: 'Shakshuka with Feta',
    servings: 2,
    mealTypes: ['Breakfast', 'Brunch'],
    cuisineTypes: ['Middle Eastern'],
    dietLabels: ['Vegetarian'],
    allergens: ['Eggs', 'Milk'],
    intoleranceTags: [],
    totalTimeMinutes: 25,
    totalCalories: 520,
    totalProtein: 26,
    totalCarbs: 30,
    totalFat: 32,
    nutritionPerServing: { calories: 260, protein: 13, carbohydrate: 15, fat: 16 },
    imageUrl: 'https://images.unsplash.com/photo-1590412200988-a436970781fa?auto=format&fit=crop&w=900&q=80',
    isOwn: false,
    canEdit: false,
    isVerified: true,
  },
];

type DemoMealPlanDetail = Record<string, unknown> & {
  id: string;
  clientId: string;
  clientName: string;
  planName: string;
  status: string;
  creationMethod: string;
  planType: string;
  durationDays: number;
  suggestions: Array<Record<string, unknown> & { day: number; date: string; meals: Record<string, unknown> }>;
};

const MEAL_SLOT_TEMPLATE = [
  { key: 'breakfast', name: 'Breakfast', time: '08:00', calorieShare: 0.25 },
  { key: 'lunch', name: 'Lunch', time: '12:30', calorieShare: 0.3 },
  { key: 'snack', name: 'Snack', time: '16:00', calorieShare: 0.15 },
  { key: 'dinner', name: 'Dinner', time: '19:00', calorieShare: 0.3 },
];

const MEAL_RECIPE_ROTATION: Record<string, string[]> = {
  breakfast: ['demo-recipe-3', 'demo-recipe-8', 'demo-recipe-2'],
  lunch: ['demo-recipe-4', 'demo-recipe-1', 'demo-recipe-6'],
  snack: ['demo-recipe-7', 'demo-recipe-2', 'demo-recipe-7'],
  dinner: ['demo-recipe-5', 'demo-recipe-6', 'demo-recipe-1'],
};

const toMealPlanRecipe = (recipe: Record<string, unknown>, targetCalories: number) => {
  const perServing = (recipe.nutritionPerServing ?? {}) as Record<string, number>;
  return {
    id: recipe.id,
    recipeId: recipe.id,
    title: recipe.title,
    name: recipe.name,
    image: recipe.imageUrl,
    imageUrl: recipe.imageUrl,
    source: 'cached',
    provider: 'internal',
    servings: 1,
    selectedServings: 1,
    isSelected: true,
    readyInMinutes: recipe.totalTimeMinutes,
    totalTimeMinutes: recipe.totalTimeMinutes,
    calories: perServing.calories ?? targetCalories,
    protein: perServing.protein ?? 0,
    carbs: perServing.carbohydrate ?? 0,
    fat: perServing.fat ?? 0,
    nutrition: {
      calories: perServing.calories ?? targetCalories,
      protein: perServing.protein ?? 0,
      carbs: perServing.carbohydrate ?? 0,
      carbohydrates: perServing.carbohydrate ?? 0,
      fat: perServing.fat ?? 0,
    },
    nutritionPerServing: recipe.nutritionPerServing,
    allergens: recipe.allergens ?? [],
    dietLabels: recipe.dietLabels ?? [],
    mealTypes: recipe.mealTypes ?? [],
  };
};

type DemoPlanDay = Record<string, unknown> & { day: number; date: string; meals: Record<string, unknown> };

/** Projects internal plan days into the `daySuggestions` shape the plan viewers normalize. */
const buildDaySuggestions = (suggestions: DemoPlanDay[]) =>
  suggestions.map((day) => ({
    dayNumber: day.day,
    date: day.date,
    meals: MEAL_SLOT_TEMPLATE.map((slot, index) => {
      const meal = (day.meals as Record<string, Record<string, unknown>>)[slot.key];
      const mealRecipes = (meal?.recipes ?? []) as Array<Record<string, unknown>>;
      const primary = mealRecipes[0] ?? {};
      const suggestionRecipe = {
        ...primary,
        nutrition: {
          calories: primary.calories,
          protein: primary.protein,
          carbohydrate: primary.carbs,
          total_fat: primary.fat,
        },
      };
      return {
        mealName: slot.name,
        mealType: slot.key,
        mealOrder: index + 1,
        mealTime: slot.time,
        targetCalories: meal?.targetCalories ?? 0,
        selectedRecipeId: primary.id ?? '',
        recipeSuggestions: [suggestionRecipe],
        recipes: mealRecipes,
        ingredients: [],
        notes: null,
      };
    }),
  }));

const buildDemoPlanDay = (dayNumber: number, startYmd: string, targetCalories: number, recipes: Record<string, unknown>[]) => {
  const date = new Date(`${startYmd}T12:00:00`);
  date.setDate(date.getDate() + (dayNumber - 1));
  const dateYmd = date.toISOString().slice(0, 10);
  const meals: Record<string, unknown> = {};
  for (const slot of MEAL_SLOT_TEMPLATE) {
    const rotation = MEAL_RECIPE_ROTATION[slot.key];
    const recipeId = rotation[(dayNumber - 1) % rotation.length];
    const recipe = recipes.find((entry) => entry.id === recipeId) ?? recipes[0];
    const slotCalories = Math.round(targetCalories * slot.calorieShare);
    meals[slot.key] = {
      mealName: slot.name,
      mealTime: slot.time,
      mealOrder: MEAL_SLOT_TEMPLATE.indexOf(slot) + 1,
      targetCalories: slotCalories,
      recipes: [toMealPlanRecipe(recipe, slotCalories)],
      customizations: {},
    };
  }
  return { day: dayNumber, dayNumber, date: dateYmd, meals };
};

const buildDemoPlanDetail = (options: {
  id: string;
  clientId: string;
  clientName: string;
  planName: string;
  creationMethod: 'ai_generated' | 'manual';
  planType: string;
  durationDays: number;
  status: 'draft' | 'completed';
  startInDays?: number;
  targetCalories?: number;
}): DemoMealPlanDetail => {
  const recipes = baseRecipes();
  const targetCalories = options.targetCalories ?? 1900;
  const startYmd = ymdInDays(options.startInDays ?? 1);
  const suggestions = Array.from({ length: options.durationDays }, (_, index) =>
    buildDemoPlanDay(index + 1, startYmd, targetCalories, recipes),
  );
  const totalMeals = options.durationDays * MEAL_SLOT_TEMPLATE.length;
  const dayNutrition = {
    calories: targetCalories,
    protein: Math.round((targetCalories * 0.25) / 4),
    carbs: Math.round((targetCalories * 0.45) / 4),
    carbohydrate: Math.round((targetCalories * 0.45) / 4),
    fat: Math.round((targetCalories * 0.3) / 9),
    total_fat: Math.round((targetCalories * 0.3) / 9),
    fiber: 28,
    sodium: 1800,
    sugar: 42,
    cholesterol: 120,
  };
  const daySuggestions = buildDaySuggestions(suggestions);
  const dailyNutrition = suggestions.map((day) => ({
    dayNumber: day.day,
    date: day.date,
    nutrition: dayNutrition,
  }));
  return {
    id: options.id,
    clientId: options.clientId,
    clientName: options.clientName,
    planName: options.planName,
    name: options.planName,
    planDate: startYmd,
    startDate: startYmd,
    endDate: ymdInDays((options.startInDays ?? 1) + options.durationDays - 1),
    durationDays: options.durationDays,
    totalDays: options.durationDays,
    days: options.durationDays,
    totalMeals,
    selectedMeals: totalMeals,
    status: options.status,
    creationMethod: options.creationMethod,
    generationType: options.creationMethod === 'ai_generated' ? 'ai_assisted' : 'manual',
    planType: options.planType,
    isComplete: options.status === 'completed',
    completionPercentage: 100,
    nutritionistId: 'demo-nutritionist',
    searchParams: { targetCalories, dietaryPreferences: [], allergies: [] },
    nutrition: {
      calories: targetCalories,
      protein: Math.round((targetCalories * 0.25) / 4),
      carbs: Math.round((targetCalories * 0.45) / 4),
      carbohydrates: Math.round((targetCalories * 0.45) / 4),
      fat: Math.round((targetCalories * 0.3) / 9),
      dailyAverage: {
        calories: targetCalories,
        protein: Math.round((targetCalories * 0.25) / 4),
        carbs: Math.round((targetCalories * 0.45) / 4),
        fat: Math.round((targetCalories * 0.3) / 9),
      },
    },
    suggestions,
    daySuggestions,
    dailyNutrition,
    createdAt: isoInDays(-2, 9, 0),
    updatedAt: new Date().toISOString(),
    expiresAt: null,
  };
};

const basePlanDetails = (): Record<string, DemoMealPlanDetail> => ({
  'demo-meal-plan-1': buildDemoPlanDetail({
    id: 'demo-meal-plan-1',
    clientId: 'demo-client-1',
    clientName: 'Amelia Hart',
    planName: 'Mediterranean Glucose Support',
    creationMethod: 'ai_generated',
    planType: 'ai_assisted',
    durationDays: 7,
    status: 'draft',
    startInDays: 1,
    targetCalories: 1840,
  }),
  'demo-meal-plan-2': buildDemoPlanDetail({
    id: 'demo-meal-plan-2',
    clientId: 'demo-client-2',
    clientName: 'Noah Patel',
    planName: 'Lean Mass Builder',
    creationMethod: 'manual',
    planType: 'manual',
    durationDays: 5,
    status: 'completed',
    startInDays: -3,
    targetCalories: 2890,
  }),
  'demo-meal-plan-3': buildDemoPlanDetail({
    id: 'demo-meal-plan-3',
    clientId: 'demo-client-4',
    clientName: 'Priya Sharma',
    planName: 'PCOS Balance Week',
    creationMethod: 'manual',
    planType: 'manual',
    durationDays: 3,
    status: 'draft',
    startInDays: 2,
    targetCalories: 1750,
  }),
  'demo-generic-plan-1': buildDemoPlanDetail({
    id: 'demo-generic-plan-1',
    clientId: '',
    clientName: '',
    planName: '7-Day Heart Health Starter',
    creationMethod: 'manual',
    planType: 'generic',
    durationDays: 7,
    status: 'completed',
    startInDays: 0,
    targetCalories: 2000,
  }),
});

const planDetailToListItem = (detail: DemoMealPlanDetail) => ({
  id: detail.id,
  planType: detail.planType,
  generationType: detail.generationType,
  clientId: detail.clientId,
  client: detail.clientId
    ? { id: detail.clientId, name: detail.clientName, email: `${String(detail.clientName).toLowerCase().replace(/\s+/g, '.')}@example.com` }
    : undefined,
  clientName: detail.clientName,
  name: detail.planName,
  planName: detail.planName,
  description: null,
  durationDays: detail.durationDays,
  days: detail.durationDays,
  startDate: detail.startDate,
  endDate: detail.endDate,
  status: detail.status === 'completed' ? 'finalized' : 'draft',
  tags: detail.planType === 'generic' ? ['Heart health'] : [],
  allergens: [],
  intoleranceTags: [],
  summary: {
    totalMeals: detail.totalMeals,
    selectedMeals: detail.selectedMeals,
    completionPercentage: detail.completionPercentage,
  },
  overallNutrition: detail.nutrition,
  targetDaily: detail.nutrition,
  createdAt: detail.createdAt,
  updatedAt: detail.updatedAt,
  activatedAt: detail.status === 'completed' ? detail.createdAt : null,
});

const createState = () => ({
  user: getDemoUser(),
  clients: baseClients(),
  appointments: baseAppointments(),
  invoices: baseInvoices(),
  recipes: baseRecipes(),
  mealPlanDetails: basePlanDetails(),
  questionnaires: [
    ...DEMO_CORE_QUESTIONNAIRE_LIST.map((entry) => ({ ...entry })),
    {
      id: 'demo-custom-glucose',
      questionnaireId: 'demo-custom-glucose',
      title: 'Glucose Monitoring Check-in',
      name: 'Glucose Monitoring Check-in',
      description: 'Weekly blood glucose review and barriers.',
      type: 'custom',
      questionsCount: 8,
      estimatedTimeMinutes: 5,
      assignedClientsCount: 1,
      responsesCount: 3,
      assigned: true,
      status: 'published',
    },
  ],
  questionnaireInstances: baseQuestionnaireInstances(),
  mealPlans: Object.values(basePlanDetails()).map(planDetailToListItem),
  notifications: [
    {
      id: 'demo-notification-1',
      recipientType: 'nutritionist',
      recipientId: 'demo-nutritionist',
      eventKey: 'questionnaire.completed',
      workflow: 'questionnaires',
      priority: 'medium',
      title: 'Questionnaire completed',
      body: 'Amelia Hart completed New Client Health Profile.',
      channels: { inApp: true, push: false, email: true },
      readAt: null,
      archivedAt: null,
      createdAt: isoInDays(-1, 12, 0),
      updatedAt: isoInDays(-1, 12, 0),
      isRead: false,
      isArchived: false,
    },
    {
      id: 'demo-notification-pending-deletion',
      recipientType: 'nutritionist',
      recipientId: 'demo-nutritionist',
      eventKey: 'client_account_deletion_requested',
      workflow: 'clients',
      priority: 'high',
      title: 'Client deleted their account',
      body: 'Jordan Hale deleted their account. They have 14 days to restore it. Until then they are paused on your roster.',
      cta: {
        label: 'Open Pending deletion',
        destination: 'client_pending_deletion',
        params: {
          clientId: 'demo-client-pending',
          purgeAt: isoInDays(12, 9, 0),
        },
      },
      channels: { inApp: true, push: true, email: false },
      readAt: null,
      archivedAt: null,
      createdAt: isoInDays(-2, 9, 0),
      updatedAt: isoInDays(-2, 9, 0),
      isRead: false,
      isArchived: false,
    },
  ],
});

export type DemoState = ReturnType<typeof createState>;

let demoState: DemoState = createState();

export const getDemoState = () => demoState;

export const resetDemoState = () => {
  demoState = createState();
  return demoState;
};

export const getDemoProfile = (): User => demoState.user;

export const getDemoQuestionnaireInstances = (clientId?: string) => {
  const instances = demoState.questionnaireInstances;
  if (!clientId) return instances;
  return instances.filter((instance) => instance.clientId === clientId);
};

export const assignDemoQuestionnaire = (clientId: string, questionnaireId: string) => {
  const alreadyAssigned = demoState.questionnaireInstances.some(
    (instance) => instance.clientId === clientId && instance.questionnaireId === questionnaireId
  );
  if (alreadyAssigned) return;

  const questionnaire = demoState.questionnaires.find(
    (entry) => entry.id === questionnaireId || entry.questionnaireId === questionnaireId
  );
  const title =
    String(questionnaire?.title || questionnaire?.name || '').trim() || DEMO_NEW_CLIENT_HEALTH_PROFILE_TITLE;
  const questionCount = Number(questionnaire?.questionsCount || 0);

  demoState.questionnaireInstances = [
    {
      instanceId: `demo-questionnaire-instance-${demoState.questionnaireInstances.length + 1}-${Date.now().toString(36)}`,
      questionnaireId,
      questionnaireTitle: title,
      questionnaireName: title,
      clientId,
      nutritionistId: 'demo-nutritionist',
      status: 'pending',
      hasResponse: false,
      createdAt: new Date().toISOString(),
      actualQuestionnaireQuestionCount: questionCount,
      initialQuestionCount: questionCount,
      answeredQuestionCount: 0,
      responses: null,
    },
    ...demoState.questionnaireInstances,
  ];

  if (questionnaire) {
    const assignedCount = demoState.questionnaireInstances.filter(
      (instance) => instance.questionnaireId === questionnaireId
    ).length;
    questionnaire.assigned = true;
    questionnaire.assignedClientsCount = assignedCount;
  }
};

export const createDemoClient = (payload: Record<string, unknown> = {}) => {
  const firstName = String(payload.firstName || 'Priya');
  const lastName = String(payload.lastName || 'Morgan');
  const client: DemoClient = {
    ...baseClients()[0],
    ...payload,
    id: `demo-client-${demoState.clients.length + 1}`,
    firstName,
    lastName,
    fullName: `${firstName} ${lastName}`,
    email: String(payload.email || `demo.client.${demoState.clients.length + 1}@example.com`),
    status: 'active',
    location: String(payload.location || 'Cambridge'),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  demoState.clients = [client, ...demoState.clients];
  assignDemoQuestionnaire(client.id, DEMO_NEW_CLIENT_HEALTH_PROFILE_ID);

  const extraQuestionnaireIds = Array.isArray(payload.questionnaireIds)
    ? payload.questionnaireIds.map((id) => String(id).trim()).filter(Boolean)
    : [];
  extraQuestionnaireIds.forEach((questionnaireId) => assignDemoQuestionnaire(client.id, questionnaireId));

  return client;
};

export const createDemoAppointment = (payload: Record<string, unknown> = {}) => {
  const clientId = String(payload.clientId || 'demo-client-1');
  const client = demoState.clients.find((entry) => entry.id === clientId) ?? demoState.clients[0];
  const startTime = String(payload.startTime || isoInDays(5, 10, 30));
  const appointment: DemoAppointment = {
    id: `demo-appointment-${demoState.appointments.length + 1}`,
    clientId: client.id,
    clientName: client.fullName,
    clientEmail: client.email,
    title: String(payload.title || payload.type || 'Initial nutrition consultation'),
    type: String(payload.title || payload.type || 'Initial nutrition consultation'),
    status: 'active',
    appointmentType: String(payload.appointmentType || 'online'),
    mode: String(payload.appointmentType || 'online'),
    location: String(payload.location || 'Google Meet'),
    meetingLink: 'https://meet.google.com/demo-new-session',
    date: startTime,
    startTime,
    endTime: String(payload.endTime || addMinutes(startTime, 60)),
    duration: Number(payload.duration || 60),
    description: String(payload.description || 'Demo-created appointment.'),
  };
  demoState.appointments = [appointment, ...demoState.appointments];
  return appointment;
};

const syncPlanList = () => {
  demoState.mealPlans = Object.values(demoState.mealPlanDetails).map(planDetailToListItem);
};

/** Recomputes viewer-facing projections after suggestions are mutated. */
const refreshPlanProjections = (detail: DemoMealPlanDetail) => {
  const days = detail.suggestions as DemoPlanDay[];
  detail.daySuggestions = buildDaySuggestions(days);
  const nutritionTemplate =
    (detail.dailyNutrition as Array<Record<string, unknown>> | undefined)?.[0]?.nutrition ?? detail.nutrition;
  detail.dailyNutrition = days.map((day) => ({ dayNumber: day.day, date: day.date, nutrition: nutritionTemplate }));
};

export const getDemoPlanDetail = (planId: string): DemoMealPlanDetail | undefined =>
  demoState.mealPlanDetails[planId];

export const listDemoPlanDetails = (): DemoMealPlanDetail[] => Object.values(demoState.mealPlanDetails);

/** Creates a new plan (manual draft, AI generation, or generic) in demo state. */
export const createDemoPlanDetail = (payload: Record<string, unknown>, creationMethod: 'manual' | 'ai_generated', planType?: string) => {
  const clientId = String(payload.clientId || '');
  const client = demoState.clients.find((entry) => entry.id === clientId);
  const count = Object.keys(demoState.mealPlanDetails).length + 1;
  const id = `demo-meal-plan-${count}-${Date.now().toString(36)}`;
  const durationDays = Math.min(Math.max(Number(payload.durationDays || payload.days || 7), 1), 30);
  const detail = buildDemoPlanDetail({
    id,
    clientId: client ? String(client.id) : '',
    clientName: client ? String(client.fullName) : '',
    planName: String(
      payload.planName ||
        payload.name ||
        (creationMethod === 'ai_generated'
          ? `AI Plan · ${client ? client.firstName : 'Client'}`
          : `Custom Plan · ${client ? client.firstName : 'Client'}`),
    ),
    creationMethod,
    planType: planType ?? (creationMethod === 'ai_generated' ? 'ai_assisted' : 'manual'),
    durationDays,
    status: 'draft',
    startInDays: 1,
    targetCalories: client ? Number(client.targetCalories) || 1900 : 1900,
  });
  demoState.mealPlanDetails[id] = detail;
  syncPlanList();
  return detail;
};

/** Copies one day of a plan onto a new (or existing) day, matching the copy-day endpoint. */
export const copyDemoPlanDay = (planId: string, sourceDay: number, targetDay?: number) => {
  const detail = demoState.mealPlanDetails[planId];
  if (!detail) return undefined;
  const source = detail.suggestions.find((entry) => entry.day === sourceDay) ?? detail.suggestions[0];
  if (!source) return undefined;
  const resolvedTarget = targetDay && targetDay > 0 ? targetDay : detail.suggestions.length + 1;
  const copied = JSON.parse(JSON.stringify({ ...source, day: resolvedTarget, dayNumber: resolvedTarget }));
  const existingIndex = detail.suggestions.findIndex((entry) => entry.day === resolvedTarget);
  if (existingIndex >= 0) {
    detail.suggestions[existingIndex] = copied;
  } else {
    detail.suggestions.push(copied);
    detail.durationDays = detail.suggestions.length;
    detail.totalDays = detail.suggestions.length;
    detail.days = detail.suggestions.length;
    detail.totalMeals = detail.suggestions.length * MEAL_SLOT_TEMPLATE.length;
    detail.selectedMeals = detail.totalMeals;
  }
  refreshPlanProjections(detail);
  detail.updatedAt = new Date().toISOString();
  syncPlanList();
  return detail;
};

/** Appends a fresh day of meals to a plan. */
export const addDemoPlanDay = (planId: string) => {
  const detail = demoState.mealPlanDetails[planId];
  if (!detail) return undefined;
  const nextDay = detail.suggestions.length + 1;
  const startYmd = String(detail.planDate ?? ymdInDays(1));
  const targetCalories = Number((detail.searchParams as Record<string, unknown>)?.targetCalories) || 1900;
  detail.suggestions.push(buildDemoPlanDay(nextDay, startYmd, targetCalories, demoState.recipes));
  detail.durationDays = nextDay;
  detail.totalDays = nextDay;
  detail.days = nextDay;
  detail.totalMeals = nextDay * MEAL_SLOT_TEMPLATE.length;
  detail.selectedMeals = detail.totalMeals;
  refreshPlanProjections(detail);
  detail.updatedAt = new Date().toISOString();
  syncPlanList();
  return detail;
};

export const finalizeDemoPlan = (planId: string) => {
  const detail = demoState.mealPlanDetails[planId];
  if (!detail) return undefined;
  detail.status = 'completed';
  detail.isComplete = true;
  detail.updatedAt = new Date().toISOString();
  syncPlanList();
  return detail;
};

export const touchDemoPlan = (planId: string) => {
  const detail = demoState.mealPlanDetails[planId];
  if (detail) {
    detail.updatedAt = new Date().toISOString();
    syncPlanList();
  }
  return detail;
};

export const getDemoMealProgram = (clientId: string) => {
  const client = demoState.clients.find((entry) => entry.id === clientId) ?? demoState.clients[0];
  const targetCalories = Number(client.targetCalories) || 1900;
  return {
    id: `demo-meal-program-${client.id}`,
    clientId: client.id,
    name: 'Standard 4-meal day',
    description: 'Breakfast, lunch, snack, and dinner distribution.',
    meals: MEAL_SLOT_TEMPLATE.map((slot, index) => ({
      id: `demo-meal-slot-${index + 1}`,
      mealName: slot.name,
      mealTime: slot.time,
      mealOrder: index + 1,
      targetCalories: Math.round(targetCalories * slot.calorieShare),
      caloriePercentage: Math.round(slot.calorieShare * 100),
    })),
  };
};

export const createDemoInvoice = (payload: Record<string, unknown> = {}) => {
  const client = demoState.clients.find((entry) => entry.id === payload.clientId) ?? demoState.clients[0];
  const invoice: DemoInvoice = {
    id: `demo-invoice-${demoState.invoices.length + 1}`,
    invoiceNo: String(payload.invoiceNo || `INV-DEMO-${1000 + demoState.invoices.length + 1}`),
    clientId: client.id,
    clientName: client.fullName,
    title: String(payload.title || 'Nutrition consultation'),
    status: 'due',
    invoiceStatus: 'due',
    totalDue: Number(payload.totalDue || payload.amount || 120),
    amount: Number(payload.totalDue || payload.amount || 120),
    currency: String(payload.currency || 'GBP'),
    dueDate: String(payload.dueDate || isoInDays(7, 0, 0)),
    invoiceDate: new Date().toISOString(),
    lineItems: Array.isArray(payload.lineItems)
      ? payload.lineItems
      : [{ description: 'Nutrition consultation', qty: 1, unitPrice: 120 }],
  };
  demoState.invoices = [invoice, ...demoState.invoices];
  return invoice;
};
